import React, { useMemo } from 'react';
import { css } from 'emotion';
import { useSelector } from 'react-redux';
import { Heading, Text, SpinningLoader } from '@weave/design-system';
import { theme } from '@weave/theme';
import { SourceContainerConfig } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { SyncEngineSourcesQueries } from 'apis/sync-engine-sources/queries';
import { selectCurrentLocationV2 } from '../../redux/actions/location';
import { SyncEngineCard } from './sync-engine-card.component';

export const SyncEngine = () => {
  const orgId = useSelector(selectCurrentLocationV2)?.orgId;

  const { data: syncEngineSources, isLoading } =
    SyncEngineSourcesQueries.useGetSyncEngineSources(
      {
        filters: {
          orgId,
          includeDeleted: false,
        },
      },
      { enabled: !!orgId }
    );

  const sources = syncEngineSources?.sources ?? [];

  const sourceIds = useMemo(
    () =>
      sources
        .map((source) => source.sourceId)
        .filter((sourceId): sourceId is string => !!sourceId),
    [sources]
  );

  const sourceContainerConfigQueries =
    SyncEngineSourcesQueries.useGetSourceContainerConfigs(sourceIds);

  const isConfigsLoading = sourceContainerConfigQueries.some((q) => q.isLoading);
  const isPageLoading = !orgId || isLoading || isConfigsLoading;

  const configsBySourceId: Record<string, SourceContainerConfig> = {};
  sourceIds.forEach((sourceId, index) => {
    const config = sourceContainerConfigQueries[index]?.data;
    if (config) {
      configsBySourceId[sourceId] = config;
    }
  });

  return (
    <div className={mainDiv}>
      <Heading level={1}>Sync Engine</Heading>
      {isPageLoading && <SpinningLoader />}
      {!isPageLoading && !sources.length && (
        <Text>No sync engine sources exist for this location.</Text>
      )}
      {!isPageLoading && (
        <div className={listDiv}>
          {sources.map((source) => (
            <SyncEngineCard
              key={source.sourceId}
              source={source}
              config={source.sourceId ? configsBySourceId[source.sourceId] : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const mainDiv = css`
  width: 100%;
  padding: ${theme.spacing(2.5)};
`;

const listDiv = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  margin-top: ${theme.spacing(2)};
`;
